// =====================================================
// INLINE — where a rebuilt paragraph puts back what is not a word: footnote, endnote and comment
// marks, pictures and equations, field codes, symbols, bookmarks, and the starts and ends of
// hyperlinks, simple fields and content controls (InlineEvent, recorded by the parser).
//
// Until stage 3 of the engine cleanup, an edited paragraph was rebuilt from its words alone:
// links, fields and bookmarks were dropped, and footnote marks, comments and pictures were added
// at the paragraph's end. Now each goes back where it was, among the diff's pieces (pieces.ts):
//
// - The revised draft's are all kept, where they are in the revised draft, so Accept All gives
//   the revised paragraph back.
// - What can be tracked (a mark, a picture, a field code, a symbol: InlineEvent.key) is matched
//   with the original's. One both drafts have in the same place is written once. One whose place
//   differs only by changed words (a footnote after "Seller" that is now after "Buyer") is written
//   once too, between the change's two sides, where both Accept All and Reject All read it in
//   its draft's place. Otherwise the revised one is an insertion and the original's a deletion,
//   so Reject All gives the original back.
// - The original's bookmarks, comments and links are not carried in: Word does not track them,
//   and the revised draft's are the ones Accept All must keep.
// =====================================================

import type { InlineEvent } from '../types';
import type { Piece } from './pieces';

/** A place in the redline paragraph: piece `i`, `k` characters into its text, or (k = -1) before
 *  the whitespace in front of it. Piece `pieces.length` is the end: k = -1 right after the last
 *  piece, k = 0 after the whitespace after it. */
export interface Loc { i: number; k: number }

export interface PlacedEvent {
  loc: Loc;
  ev: InlineEvent;
  /** equal: written as it is; insert / delete: tracked as inserted (revised draft only) or
   *  deleted (original only) */
  status: 'equal' | 'insert' | 'delete';
  /** the original's (its relationship ids are the original's: tagCrossRelIds) */
  fromOrig: boolean;
  /** equal: the original's event it was matched with (a footnote mark: the note it names there) */
  origEv?: InlineEvent;
}

interface Span { i: number; s: number; e: number }

/** Each word of one side and the pieces that draw it, with the characters each piece covers. */
function sideSpans(pieces: Piece[], side: 'orig' | 'rev'): Map<number, Span[]> {
  const byWord = new Map<number, Span[]>();
  const next = new Map<number, number>();
  pieces.forEach((p, i) => {
    const d = p.entry;
    if (side === 'rev' ? !p.fromRev : (p.fromRev && d.type !== 'equal')) return;
    const w = side === 'rev' ? d.revWordIdx : d.origWordIdx;
    if (w == null) return;
    const ci = side === 'rev' ? d.revCharIdx : d.origCharIdx;
    const s = ci != null ? ci : (next.get(w) ?? 0);
    const e = s + d.value.length;
    next.set(w, e);
    const list = byWord.get(w);
    if (list) list.push({ i, s, e }); else byWord.set(w, [{ i, s, e }]);
  });
  return byWord;
}

/** Where an event of one side goes among the pieces, or null when its word is not drawn. */
function locate(byWord: Map<number, Span[]>, ev: InlineEvent, words: number, end: number): Loc | null {
  if (ev.w >= words) return { i: end, k: 0 };
  const sp = byWord.get(ev.w);
  if (!sp || !sp.length) return null;
  if (ev.c < 0) return { i: sp[0].i, k: -1 };
  if (ev.c === 0) { const f = sp.find(x => x.s === 0); return f ? { i: f.i, k: 0 } : { i: sp[0].i, k: 0 }; }
  // right after a piece: before what follows it (the next piece's whitespace)
  for (const x of sp) if (x.e === ev.c) return { i: x.i + 1, k: -1 };
  for (const x of sp) if (x.s < ev.c && ev.c < x.e) return { i: x.i, k: ev.c - x.s };
  return { i: sp[sp.length - 1].i + 1, k: -1 };
}

export const cmpLoc = (a: Loc, b: Loc): number => (a.i - b.i) || (a.k - b.k);

/**
 * Where one event both drafts have can be written once, given its place in each (null when it
 * cannot: unchanged text lies between the two places, or changes on both sides that cross).
 * Accept All reads it after the revised draft's text before `rev`, Reject All after the
 * original's text before `orig`; one place satisfies both when the pieces between the two
 * places are all changes, with the revised side's before the original side's (rev after orig)
 * or the original's before the revised's (rev before orig).
 */
function mergedLoc(pieces: Piece[], orig: Loc, rev: Loc): Loc | null {
  const c = cmpLoc(orig, rev);
  if (c === 0) return rev;
  const [a, b] = c < 0 ? [orig, rev] : [rev, orig];
  const revIdx: number[] = [], origIdx: number[] = [];
  for (let i = Math.max(0, a.i); i < pieces.length && i <= b.i; i++) {
    // piece i's text runs from (i, 0) to (i + 1, -1): does any of it lie between a and b?
    if (!(cmpLoc(a, { i: i + 1, k: -1 }) < 0 && cmpLoc(b, { i, k: 0 }) > 0)) continue;
    const p = pieces[i];
    if (p.entry.type === 'equal') return null;
    (p.fromRev ? revIdx : origIdx).push(i);
  }
  if (!origIdx.length) return rev;
  if (!revIdx.length) return orig;
  if (c < 0 && Math.max(...revIdx) < Math.min(...origIdx)) return { i: Math.min(...origIdx), k: -1 };
  if (c > 0 && Math.max(...origIdx) < Math.min(...revIdx)) return { i: Math.max(...origIdx) + 1, k: -1 };
  return null;
}

/**
 * The events of both sides of an edited paragraph, placed among its pieces and in the order they
 * are written (by place, then each draft's own order). `origWords` / `revWords`: each side's
 * word count. Null when the revised side records none (its events are unknown: the caller keeps
 * the old way of appending them).
 */
export function placeInline(pieces: Piece[], orig: { inline?: InlineEvent[]; words: number } | null,
  rev: { inline?: InlineEvent[]; words: number } | null): PlacedEvent[] | null {
  if (!rev?.inline && !orig?.inline) return null;
  const end = pieces.length;
  const revSpans = sideSpans(pieces, 'rev'), origSpans = sideSpans(pieces, 'orig');
  // the revised draft's events (all), and the original's that can be tracked
  const R = (rev?.inline || []).map(ev => ({ ev, loc: locate(revSpans, ev, rev!.words, end) || { i: end, k: 0 } }));
  const O = (orig?.inline || []).filter(ev => ev.key)
    .map(ev => ({ ev, loc: locate(origSpans, ev, orig!.words, end) }))
    .filter((x): x is { ev: InlineEvent; loc: Loc } => !!x.loc);

  // match the original's trackable events to the revised draft's in order (longest common
  // subsequence): same key, and a place both can share
  const n = O.length, m = R.length;
  const merged: (Loc | null)[][] = O.map(o => R.map(r => (r.ev.key && r.ev.key === o.ev.key) ? mergedLoc(pieces, o.loc, r.loc) : null));
  const L = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    L[i][j] = merged[i][j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);

  const out: (PlacedEvent & { order: number })[] = [];
  let order = 0;
  for (let i = 0, j = 0; i < n || j < m;) {
    if (i < n && j < m && merged[i][j] && L[i][j] === L[i + 1][j + 1] + 1) {
      out.push({ loc: merged[i][j]!, ev: R[j].ev, origEv: O[i].ev, status: 'equal', fromOrig: false, order: order++ });
      i++; j++;
    } else if (i < n && (j >= m || L[i + 1][j] >= L[i][j + 1])) {
      out.push({ loc: O[i].loc, ev: O[i].ev, status: 'delete', fromOrig: true, order: order++ });
      i++;
    } else {
      out.push({ loc: R[j].loc, ev: R[j].ev, status: R[j].ev.key ? 'insert' : 'equal', fromOrig: false, order: order++ });
      j++;
    }
  }
  out.sort((a, b) => cmpLoc(a.loc, b.loc) || (a.order - b.order));
  return out.map(({ order: _o, ...p }) => p);
}

/** Whether two versions of a paragraph differ in what can be tracked besides its words: a footnote
 *  or endnote mark, a picture, a field, a symbol (InlineEvent.key, in order). An unchanged paragraph
 *  (or cell paragraph) that differs so is written as an edited one, all its words unchanged, so the
 *  mark is tracked. */
export function marksDiffer(o: { inline?: InlineEvent[] }, r: { inline?: InlineEvent[] }): boolean {
  if (!o.inline || !r.inline) return false;
  const keys = (x: { inline?: InlineEvent[] }) => x.inline!.filter(e => e.key).map(e => e.key).join('\u0001');
  return keys(o) !== keys(r);
}
